// src/components/profile/ExecutiveOverview/EvidenceBand.jsx
import React, { useState } from 'react';
import { ExternalLink, Database, FileText, Clock } from 'lucide-react';

export default function EvidenceBand({ items, evidence }) {
  const [showTooltip, setShowTooltip] = useState(null);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Show items if available (external evidence)
  if (items && items.length > 0) {
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Evidence & Sources
        </div>
        <ul className="grid md:grid-cols-2 gap-3">
          {items.slice(0, 2).map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-700 leading-5">
                  {item.label}
                </div>
                {item.source && (
                  <div className="text-xs text-slate-500 mt-1">
                    {item.link ? (
                      <a 
                        href={item.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors"
                      >
                        {item.source}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      item.source
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
        
        {/* Add provenance micro-badges if evidence provided */}
        {evidence && evidence.length > 0 && (
          <div className="border-t mt-3 pt-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">Data:</span>
            {evidence.map((source, i) => (
              <div key={i} className="relative">
                <button
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white hover:bg-slate-100 rounded text-xs text-slate-600 transition-colors"
                  onMouseEnter={() => setShowTooltip(i)}
                  onMouseLeave={() => setShowTooltip(null)}
                >
                  {source.table?.includes('client') ? (
                    <FileText className="w-3 h-3" />
                  ) : (
                    <Database className="w-3 h-3" />
                  )}
                </button>
                
                {showTooltip === i && (
                  <div className="absolute bottom-full left-0 mb-2 z-10 w-64 p-2 bg-slate-900 text-white text-xs rounded shadow-lg">
                    <div>
                      <span className="text-slate-400">Data source: </span>
                      <span className="font-medium">{source.table}.{source.field}</span>
                    </div>
                    {source.updatedAt && (
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{formatDate(source.updatedAt)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Fallback: thin provenance band only
  if (evidence && evidence.length > 0) {
    return (
      <div className="border-t pt-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 font-medium">Data sources:</span>
          <div className="flex gap-2">
            {evidence.map((source, i) => (
              <div key={i} className="relative">
                <button
                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 hover:bg-slate-100 rounded text-xs text-slate-600"
                  onMouseEnter={() => setShowTooltip(i)}
                  onMouseLeave={() => setShowTooltip(null)}
                >
                  {source.table?.includes('client') ? (
                    <FileText className="w-3 h-3" />
                  ) : (
                    <Database className="w-3 h-3" />
                  )}
                  <span className="font-medium">
                    {source.table?.split('_').slice(0, 2).join(' ')}
                  </span>
                </button>
                
                {showTooltip === i && (
                  <div className="absolute bottom-full left-0 mb-2 z-10 w-64 p-2 bg-slate-900 text-white text-xs rounded shadow-lg">
                    <div>
                      <span className="text-slate-400">Data source: </span>
                      <span className="font-medium">{source.table}.{source.field}</span>
                    </div>
                    {source.updatedAt && (
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{formatDate(source.updatedAt)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return null;
}